import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Tag,
  Progress,
  Button,
  Space,
  Descriptions,
  Divider,
  Tooltip,
  Typography,
  Segmented,
} from "antd";
import {
  CheckCircleOutlined,
  WarningOutlined,
  SyncOutlined,
  ReloadOutlined,
  ExperimentOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import {
  getJobRequirements,
  runMRP,
  calculateJobBOM,
} from "../../api/mrpApi";
import FulfillmentBadge from "./FulfillmentBadge";

const { Text } = Typography;

/**
 * Detailed material-requirements view for a single job.
 *
 * Designed to be embedded inside JobDetail or rendered standalone.
 *
 * API response shape (from API Guide §9.4):
 *  {
 *    job_number, item_name, production_quantity, fulfillment_status,
 *    requirements: [{ stage, material_type, material_spec, required_qty,
 *                     unit, material_id, material_code, material_name,
 *                     available_qty, reserved_qty, shortage_qty,
 *                     status, fulfillment_pct }]
 *  }
 */
const JobRequirements = ({ jobId, jobNumber }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [bomData, setBomData] = useState(null);
  const [viewMode, setViewMode] = useState("summary"); // "summary" | "detailed"

  useEffect(() => {
    if (jobId) {
      fetchRequirements();
    }
  }, [jobId]);

  const fetchRequirements = async () => {
    setLoading(true);
    try {
      const result = await getJobRequirements(jobId);
      setData(result);
    } catch (error) {
      // Silently fail — requirements may not exist yet
      console.warn("No requirements found for job", jobId);
    }
    setLoading(false);
  };

  const handleCalculateBOM = async () => {
    setLoading(true);
    try {
      const result = await calculateJobBOM(jobId);
      setBomData(result);
      toast.success("BOM calculated successfully");
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to calculate BOM"
      );
    }
    setLoading(false);
  };

  const handleRunMRP = async () => {
    setLoading(true);
    try {
      const result = await runMRP(jobId);
      toast.success(
        `MRP completed — ${result.total_requirements} requirement(s) saved`
      );
      // Refresh the requirements view
      fetchRequirements();
      setBomData(null);
    } catch (error) {
      toast.error(error.response?.data?.message || "MRP failed");
    }
    setLoading(false);
  };

  // Use saved requirements first, fall back to BOM preview
  const requirements = data?.requirements || bomData?.requirements || [];

  // ── Consolidate by material for summary view ───────────────
  const consolidatedRequirements = React.useMemo(() => {
    const map = {};
    for (const r of requirements) {
      const key = r.material_id || r.material_code || r.material?.code || `${r.material_type}-${(r.material_name || r.material?.name || "unknown")}`;
      if (!map[key]) {
        const name = r.material_name || r.material?.name || "Unknown";
        const code = r.material_code || r.material?.code || "";
        map[key] = {
          key,
          material_name: name,
          material_code: code,
          material_type: r.material_type || r.material?.category,
          material_spec: r.material_spec,
          unit: r.unit,
          required_qty: 0,
          available_qty: r.available_qty || 0,
          shortage_qty: 0,
          stages: [],
          required_for_machine: r.required_for_machine || null,
        };
      }
      map[key].required_qty += r.required_qty || 0;
      map[key].shortage_qty += r.shortage_qty || 0;
      if (r.stage && !map[key].stages.includes(r.stage)) {
        map[key].stages.push(r.stage);
      }
    }
    return Object.values(map).map((m) => ({
      ...m,
      fulfillment_pct: m.required_qty > 0 ? Math.min(100, Math.round((m.available_qty / m.required_qty) * 100)) : 100,
      status: m.available_qty >= m.required_qty ? "available" : m.available_qty > 0 ? "shortage" : "unavailable",
    }));
  }, [requirements]);

  const jobRollSize = data?.roll_size || bomData?.roll_size;

  // ── Detailed columns (per-stage breakdown) ─────────────────
  const detailedColumns = [
    {
      title: "Stage", dataIndex: "stage", key: "stage", width: 120,
      render: (stage) => <Tag>{stage}</Tag>,
    },
    {
      title: "Material", key: "material",
      render: (_, record) => {
        const name = record.material_name || record.material?.name || "Unknown";
        const code = record.material_code || record.material?.code || "";
        return (<div><div>{name}</div>{code && <Text type="secondary" style={{ fontSize: 11 }}>{code}</Text>}</div>);
      },
    },
    {
      title: "Type", dataIndex: "material_type", key: "material_type", width: 130,
      render: (type, record) => {
        const rs = record.material_spec?.width || (type === "PAPER" ? jobRollSize : null);
        return (
          <Space direction="vertical" size={0}>
            {type ? <Tag color="geekblue">{type}</Tag> : "-"}
            {type === "PAPER" && rs ? <Tag color="orange" style={{ fontWeight: 700, fontSize: 12 }}>RS: {rs}"</Tag> : null}
          </Space>
        );
      },
    },
    {
      title: "Required", dataIndex: "required_qty", key: "required_qty", width: 110,
      render: (qty, record) => <Text>{typeof qty === "number" ? qty.toFixed(2) : qty} {record.unit}</Text>,
    },
    {
      title: "Available", dataIndex: "available_qty", key: "available_qty", width: 110,
      render: (qty, record) => <Text style={{ color: qty >= record.required_qty ? "#52c41a" : "#faad14" }}>{typeof qty === "number" ? qty.toFixed(2) : qty} {record.unit}</Text>,
    },
    {
      title: "Status", dataIndex: "status", key: "status", width: 130,
      render: (_, record) => {
        if (record.available_qty >= record.required_qty) return <Tag color="green" icon={<CheckCircleOutlined />}>Available</Tag>;
        if (record.available_qty > 0) return <Tag color="orange">Shortage</Tag>;
        return <Tag color="red">Unavailable</Tag>;
      },
    },
  ];

  // ── Summary columns (consolidated by material) ─────────────
  const summaryColumns = [
    {
      title: "Material", key: "material",
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 600 }}>{record.material_name}</div>
          {record.material_code && <Text type="secondary" style={{ fontSize: 11 }}>{record.material_code}</Text>}
        </div>
      ),
    },
    {
      title: "Type", dataIndex: "material_type", key: "material_type", width: 130,
      render: (type, record) => {
        const rs = record.material_spec?.width || (type === "PAPER" ? jobRollSize : null);
        return (
          <Space direction="vertical" size={0}>
            {type ? <Tag color="geekblue">{type}</Tag> : "-"}
            {type === "PAPER" && rs ? <Tag color="orange" style={{ fontWeight: 700, fontSize: 12 }}>RS: {rs}"</Tag> : null}
          </Space>
        );
      },
    },
    {
      title: "Used In Stages", dataIndex: "stages", key: "stages", width: 200,
      render: (stages) => <Space size={[2, 4]} wrap>{(stages || []).map((s) => {
        const label = s === "die_cutting" ? "Casting" : s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
        return <Tag key={s} style={{ fontSize: 11 }}>{label}</Tag>;
      })}</Space>,
    },
    {
      title: "Total Required", dataIndex: "required_qty", key: "required_qty", width: 130,
      render: (qty, record) => <Text strong>{typeof qty === "number" ? qty.toFixed(2) : qty} {record.unit}</Text>,
    },
    {
      title: "Available", dataIndex: "available_qty", key: "available_qty", width: 110,
      render: (qty, record) => <Text style={{ color: qty >= record.required_qty ? "#52c41a" : "#faad14" }}>{typeof qty === "number" ? qty.toFixed(2) : qty} {record.unit}</Text>,
    },
    {
      title: "Status", dataIndex: "status", key: "status", width: 130,
      render: (_, record) => {
        if (record.available_qty >= record.required_qty) return <Tag color="green" icon={<CheckCircleOutlined />}>Available</Tag>;
        if (record.available_qty > 0) return <Tag color="orange">Shortage</Tag>;
        return <Tag color="red">Unavailable</Tag>;
      },
    },
  ];

  // Determine source of requirements
  const source = data?.source || bomData?.source;
  const hasInstructions = source === "instructions";

  const NON_CONSUMABLE_TYPES = ["DIE", "PLATE", "PRINTING_SCREEN", "FLEXO_BLOCK", "SCREEN"];
  const getType = (r) => r.material_type || r.material?.category || "";
  const activeData = viewMode === "summary" ? consolidatedRequirements : requirements;
  const activeColumns = viewMode === "summary" ? summaryColumns : detailedColumns;
  const consumableReqs = activeData.filter((r) => !NON_CONSUMABLE_TYPES.includes(getType(r)));
  const nonConsumableReqs = activeData.filter((r) => NON_CONSUMABLE_TYPES.includes(getType(r)));

  return (
    <Card
      title={
        <Space>
          <ExperimentOutlined />
          <span>Material Requirements{jobNumber ? ` — ${jobNumber}` : ""}</span>
          {data?.fulfillment_status && (
            <FulfillmentBadge status={data.fulfillment_status} />
          )}
        </Space>
      }
      extra={
        <Space>
          <Segmented
            size="small"
            value={viewMode}
            onChange={setViewMode}
            options={[
              { label: "Summary", value: "summary" },
              { label: "By Stage", value: "detailed" },
            ]}
          />
          <Tooltip title="Calculate Bill of Materials (preview only, not saved)">
            <Button
              onClick={handleCalculateBOM}
              loading={loading}
              icon={<ExperimentOutlined />}
            >
              Preview BOM
            </Button>
          </Tooltip>
          <Button
            type="primary"
            onClick={handleRunMRP}
            loading={loading}
            icon={<ReloadOutlined />}
          >
            Run MRP
          </Button>
        </Space>
      }
      style={{ marginBottom: 16 }}
    >
      {/* Job summary header */}
      {data && (
        <>
          <Descriptions size="small" column={{ xs: 1, sm: 2, md: 4 }}>
            <Descriptions.Item label="Job #">
              {data.job_number}
            </Descriptions.Item>
            <Descriptions.Item label="Item">
              {data.item_name}
            </Descriptions.Item>
            <Descriptions.Item label="Production Qty">
              {data.production_quantity?.toLocaleString()}
            </Descriptions.Item>
            <Descriptions.Item label="Roll Size">
              {data.roll_size ? `${data.roll_size}"` : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Overall Status">
              <FulfillmentBadge
                status={data.fulfillment_status || data.overall_status}
                showLabel
              />
            </Descriptions.Item>
          </Descriptions>
          {source && (
            <Tag
              color={hasInstructions ? "green" : "orange"}
              style={{ marginTop: 8 }}
            >
              {hasInstructions
                ? "✓ Based on Work Instructions"
                : "⚠ Based on Job Specs (no instructions found)"}
            </Tag>
          )}
          <Divider style={{ margin: "12px 0" }} />
        </>
      )}

      {/* BOM preview notice */}
      {bomData && !data && (
        <>
          <Descriptions size="small" column={{ xs: 1, sm: 2, md: 3 }}>
            <Descriptions.Item label="Job #">
              {bomData.job_number}
            </Descriptions.Item>
            <Descriptions.Item label="Item">
              {bomData.item_name}
            </Descriptions.Item>
            <Descriptions.Item label="Production Qty">
              {bomData.production_quantity?.toLocaleString()}
            </Descriptions.Item>
            <Descriptions.Item label="Roll Size">
              {bomData.roll_size ? `${bomData.roll_size}"` : "-"}
            </Descriptions.Item>
          </Descriptions>
          <Space style={{ marginBottom: 12 }}>
            <Tag color="blue">BOM Preview — Run MRP to save requirements</Tag>
            {source && (
              <Tag color={hasInstructions ? "green" : "orange"}>
                {hasInstructions ? "From Work Instructions" : "From Job Specs"}
              </Tag>
            )}
          </Space>
          <Divider style={{ margin: "12px 0" }} />
        </>
      )}

      <Text strong style={{ display: "block", marginBottom: 8 }}>Materials (Consumables)</Text>
      <Table
        columns={activeColumns}
        dataSource={consumableReqs}
        rowKey={(r, idx) =>
          r.key || r.material_id || `${r.stage}-${r.material_code}-${idx}`
        }
        loading={loading}
        pagination={false}
        size="small"
        scroll={{ x: 800 }}
        locale={{
          emptyText: "No consumable requirements",
        }}
      />

      {nonConsumableReqs.length > 0 && (
        <>
          <Divider style={{ margin: "16px 0 8px" }} />
          <Text strong style={{ display: "block", marginBottom: 8 }}>Non-Consumables (Plates, Dies, Screens, Blocks)</Text>
          <Table
            columns={[
              ...activeColumns,
              {
                title: "Required By Machine",
                dataIndex: "required_for_machine",
                key: "required_for_machine",
                width: 160,
                render: (val) => val
                  ? <Tag color="volcano">{val === "die_cutting" ? "Die Cutting" : val.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}</Tag>
                  : <Tag>Any</Tag>,
              },
            ]}
            dataSource={nonConsumableReqs}
            rowKey={(r, idx) =>
              r.key || r.material_id || `${r.stage}-${r.material_code}-nc-${idx}`
            }
            loading={loading}
            pagination={false}
            size="small"
            scroll={{ x: 800 }}
            locale={{
              emptyText: "No non-consumable requirements",
            }}
          />
        </>
      )}
    </Card>
  );
};

export default JobRequirements;
